// Imports
import { faUser } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Tippy from '@tippyjs/react';
import { DateTime } from 'luxon';
import { setLightness, setSaturation } from 'polished';
import React from 'react';
import styled from 'styled-components/macro';
import tippyProps from '../../../../../../../shareables/foundation/front-end/utils/tippy-props';
import theme from '../../../../../../../shareables/theme';
import { GetRecipeResponse } from '../../../../../../../shareables/types/api/main/website/page/type/213/recipe/get';
import { GetRecipesResponse } from '../../../../../../../shareables/types/api/main/website/page/type/213/recipes/get';
import { GetTopicResponse } from '../../../../../../../shareables/types/api/main/website/page/type/215/topic/get';
import { GetTopicsResponse } from '../../../../../../../shareables/types/api/main/website/page/type/215/topics/get';
import { GetListingResponse } from '../../../../../../../shareables/types/api/main/website/page/type/216/listing/get';
import { GetListingsResponse } from '../../../../../../../shareables/types/api/main/website/page/type/216/listings/get';
import UserAvatar from '../../../../../shared-components/user-avatar';


// Styled components
interface WrapperProps {
	moreSpace?: boolean;
}

const Wrapper = styled.small<WrapperProps>`
	display: flex !important;
	align-items: center;
	white-space: pre-wrap;
	
	&:not(:last-child) {
		margin-bottom: ${({ moreSpace }) => (moreSpace ? '1rem' : '0.5714em')};
	}
`;

const ProfileAvatar = styled(UserAvatar)`
	display: flex;
	align-items: center;
	justify-content: center;
	
	min-width: 1rem;
	width: 1rem !important;
	height: 1rem;
	
	font-size: 0.5rem;
	
	border-radius: 50%;
	margin-right: 0.375rem;
	box-shadow: 0 0 0.25rem rgba(0, 0, 0, 0.1);
`;

const ProfileIcon = styled.div`
	min-width: 1rem;
	width: 1rem !important;
	height: 1rem;
	margin-right: 0.375rem;
	
	font-size: 0.5rem;
	color: ${setSaturation(0.56, setLightness(0.69, theme.colors.info))};
	
	box-shadow: 0 0 0.25rem rgba(0, 0, 0, 0.1);
	border-radius: 50%;
	
	background-color: #fff;
	text-align: center;
	
	svg {
		padding: 0.25rem 0;
	}
`;

const Contents = styled.span`
	padding-top: 0.125rem;
	padding-bottom: 0.125rem;
	
	td & {
		padding-top: 0.095235rem;
		padding-bottom: 0.095235rem;
	}
	
	line-height: 1.2;
`;


// Define the accepted props
interface Props {
	post:
		| GetTopicsResponse[0]
		| GetTopicResponse
		| GetTopicResponse['comments'][0]
		| GetListingsResponse[0]
		| GetListingResponse
		| GetRecipesResponse[0]
		| GetRecipeResponse;
	created: string;
	action: string;
	moreSpace?: boolean;
	nameFallback?: string;
	className?: string;
}


// Function component
const Byline: React.FC<Props> = ({ post, created, action, moreSpace, nameFallback, className }) => {
	// Determine relative time
	const createdDateTime = DateTime.fromISO(created);
	const relativeTime =
		Math.abs(createdDateTime.diffNow('seconds').seconds) < 60 ? 'just now' : createdDateTime.toRelative();
	
	
	// Return JSX
	return (
		<Wrapper className={className} moreSpace={moreSpace}>
			{!post.submitter && (
				<ProfileIcon>
					<FontAwesomeIcon icon={faUser} />
				</ProfileIcon>
			)}
			{post.submitter &&
				(post.submitter?.profilePhotoURL ? (
					<ProfileAvatar as='img' src={post.submitter.profilePhotoURL} alt='Profile avatar' />
				) : (
					<ProfileAvatar firstName={post.submitter.firstName} lastName={post.submitter.lastName} />
				))}
			
			<Contents>
				{post.submitter?.formattedName ?? nameFallback ?? 'No longer exists'} {action}{' '}
				<Tippy
					{...tippyProps}
					content={new Date(created).toLocaleDateString(undefined, {
						month: 'long',
						day: 'numeric',
						year: 'numeric',
						hour: 'numeric',
						minute: '2-digit',
					})}
				>
					<span>{relativeTime}</span>
				</Tippy>
			</Contents>
		</Wrapper>
	);
};

export default Byline;
